import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  Loader,
  NothingFoundAlert,
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
  Typography,
} from 'melp-design/components';
import { ROUTES } from 'config';
import { useRouteMatch } from 'react-router-dom';
import { useRouteParams } from 'utils/useIdParam';
import {
  COLOR_BY_PUBLISH_STATUS,
  useAdminDiscount,
} from 'store/admin-discounts';
import { DiscountActions, ContentReviewPendingChip } from 'containers/admin';

export const AdminDiscountLayout: FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { id: discountId } = useRouteParams(
    ROUTES.admin.discounts.details.settings,
  );

  const { data: discount, isLoading } = useAdminDiscount(discountId);

  if (discountId && isLoading) return <Loader />;
  if (discountId && !discount) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <PageHeaderLayout
        breadcrumbs={[
          {
            label: t('discounts.title'),
            url: ROUTES.admin.discounts.list,
          },
          {
            label: !discountId
              ? t('discounts.actions.create')
              : discount
              ? discount.name
              : '',
          },
        ]}
        under={
          discount ? (
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                color={COLOR_BY_PUBLISH_STATUS[discount.publishStatus]}
              >
                {t(`status.${discount.publishStatus}`)}
              </Typography>
              <ContentReviewPendingChip liveEntityId={discount.id} />
            </Stack>
          ) : undefined
        }
        side={
          discount ? (
            <DiscountActions id={discount.id} name={discount.name} />
          ) : null
        }
      />

      <PageContentLayout
        sidebar={
          <SideMenu
            tabs={[
              {
                key: 'settings',
                label: t('discounts.details.title'),
                url: ROUTES.admin.discounts.details.settings.replace(
                  ':id',
                  discountId,
                ),
                isActive:
                  path === ROUTES.admin.discounts.details.settings ||
                  path === ROUTES.admin.discounts.create,
                disabled: !discount,
              },
              ...(discount
                ? [
                    {
                      key: 'codes',
                      label: t('discounts.codes.title'),
                      url: ROUTES.admin.discounts.details.codes.replace(
                        ':id',
                        discountId,
                      ),
                      isActive: path === ROUTES.admin.discounts.details.codes,
                    },
                  ]
                : []),
            ]}
          />
        }
      >
        {children}
      </PageContentLayout>
    </Stack>
  );
};
